import PropTypes from 'prop-types';
import React from 'react';
import { DropTarget } from 'react-dnd';

import { addToLabel } from '@api/v4/bulk_actions/assets';

const LabelContent = ({
  childrenPanelId,
  connectDropTarget,
  hasChildren,
  indentationLevel,
  isExpanded,
  isHovered,
  labelId,
  name,
  onLabelSelect,
  toggleExpand,
}) => {
  // Branches use a real heading for outline/AT; leaves use a div so the tree is not all h3s.
  const NameShell = hasChildren ? 'h3' : 'div';
  const depthPaddingPx = 12 + indentationLevel * 20;

  const handleSelect = (e) => {
    const clickedExpandIcon = hasChildren
      && e.target instanceof Element
      && e.target.closest('.label-expand-icon');

    // Mouse users can toggle via the chevron without changing the selected label.
    if (clickedExpandIcon) {
      toggleExpand(labelId);
      return;
    }

    onLabelSelect(labelId);

    // Accordion pattern: header activation must expand and collapse the panel.
    // (Keyboard/screen-reader users activate the whole button, not the chevron.)
    if (hasChildren) {
      toggleExpand(labelId);
    }
  };

  const handleKeyDown = (e) => {
    if (!hasChildren) return;

    // Tree-style arrows as an additional expand/collapse mechanism.
    if (e.key === 'ArrowRight' && !isExpanded) {
      e.preventDefault();
      toggleExpand(labelId, true, true);
    } else if (e.key === 'ArrowLeft' && isExpanded) {
      e.preventDefault();
      toggleExpand(labelId);
    }
  };

  return connectDropTarget(
    <div
      className={`label-name-container label-tree-item ${isHovered ? 'hover' : ''}`}
      style={{ paddingLeft: `${depthPaddingPx}px` }}
    >
      <div className="label-tree-row">
        {!hasChildren && <div aria-hidden className="label-chevron-cell" />}
        <NameShell className="name-copy" title={name}>
          <button
            {...(hasChildren && {
              'aria-controls': childrenPanelId,
              'aria-expanded': isExpanded,
            })}
            className="select-button"
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            type="button"
          >
            {hasChildren && <span aria-hidden className="bff-caret-down icon label-expand-icon" />}
            <span aria-hidden className="bff-label icon" />
            <span className="label-name-text">{name}</span>
          </button>
        </NameShell>
      </div>
    </div>
  );
};

LabelContent.propTypes = {
  childrenPanelId: PropTypes.string,
  connectDropTarget: PropTypes.func.isRequired,
  hasChildren: PropTypes.bool,
  indentationLevel: PropTypes.number,
  isExpanded: PropTypes.bool,
  isHovered: PropTypes.bool,
  labelId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onLabelSelect: PropTypes.func.isRequired,
  toggleExpand: PropTypes.func.isRequired,
};

LabelContent.defaultProps = {
  childrenPanelId: undefined,
  hasChildren: false,
  indentationLevel: 0,
  isExpanded: false,
  isHovered: false,
};

const assetTarget = {
  drop({ labelId }, monitor) {
    const { id, selectedAssetKeys } = monitor.getItem();
    addToLabel(Array.from(new Set([...selectedAssetKeys, id])), labelId);
  }
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isHovered: monitor.isOver(),
});

export default DropTarget('asset-label', assetTarget, dropCollect)(LabelContent);
